import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { GstSlabService, CreateGstSlabDto, GstSlab } from '../gst-slab.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-gst-slab-create',
  templateUrl: './create-gstslab.html',
  styleUrls: ['./create-gstslab.scss'],
  standalone: true,
  imports: [RouterLink, FormsModule],
})
export class GstSlabCreateComponent implements OnInit {
  gstSlab: Partial<CreateGstSlabDto & { id?: string }> = {
    displayLabel: '',
    value: undefined,
    category: undefined,
    description: '',
    sortOrder: undefined,
  };

  isSubmitting = false;
  slabId?: string;

  constructor(
    private gstSlabService: GstSlabService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // Load draft if available
    this.loadDraft();

    // Check if editing
    this.slabId = this.route.snapshot.paramMap.get('id') || undefined;
    if (this.slabId) {
      this.loadSlabForEdit(this.slabId);
    }
  }

  /** Load GST slab data for editing */
  loadSlabForEdit(id: string) {
    this.gstSlabService.getGstSlabById(id).subscribe({
      next: (res: GstSlab) => {
        this.gstSlab = { ...res };
      },
      error: (err) => {
        console.error('Failed to load slab for edit:', err);
        this.toastr.error('Failed to load GST slab details.', 'Error');
        this.router.navigate(['/gst-slab']);
      },
    });
  }

  saveAsDraft(): void {
    localStorage.setItem('gstSlabDraft', JSON.stringify(this.gstSlab));
    this.toastr.info('Draft saved locally.', 'Draft');
  }

  loadDraft(): void {
    const draft = localStorage.getItem('gstSlabDraft');
    if (draft) {
      this.gstSlab = JSON.parse(draft);
      this.toastr.info('Loaded saved draft.', 'Draft');
    }
  }

  submitGstSlab(): void {
    if (
      !this.gstSlab.displayLabel ||
      this.gstSlab.value === undefined ||
      !this.gstSlab.category
    ) {
      this.toastr.warning(
        'Display Label, Rate %, and Category are required.',
        'Validation Failed'
      );
      return;
    }

    this.isSubmitting = true;

    const payload: CreateGstSlabDto = {
      displayLabel: this.gstSlab.displayLabel!,
      value: Number(this.gstSlab.value!),
      description: this.gstSlab.description || '',
      category: this.gstSlab.category!,
      sortOrder: this.gstSlab.sortOrder ?? 0,
    };

    if (this.slabId) {
      // Editing existing slab
      this.gstSlabService.updateGstSlab(this.slabId, payload).subscribe({
        next: () => {
          this.isSubmitting = false;
          localStorage.removeItem('gstSlabDraft');
          this.toastr.success('GST Slab updated successfully!', 'Success');
          this.router.navigate(['/gst-slab']);
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error('Update failed:', err);
          const apiErrors =
            err.error?.message?.join(', ') ||
            'Please check console for details.';
          this.toastr.error(`Details: ${apiErrors}`, 'Update Failed');
        },
      });
    } else {
      // Creating new slab
      this.gstSlabService.createGstSlab(payload).subscribe({
        next: () => {
          this.isSubmitting = false;
          localStorage.removeItem('gstSlabDraft');
          this.toastr.success('GST Slab created successfully!', 'Success');
          this.router.navigate(['/gst-slab']);
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error('Creation failed:', err);
          const apiErrors =
            err.error?.message?.join(', ') ||
            'Please check console for details.';
          this.toastr.error(`Details: ${apiErrors}`, 'Creation Failed');
        },
      });
    }
  }
}
